import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  deleteUser,
  getListUser,
  resetUsernamePasswordUser,
  setUserEnabled,
} from "../../services";
import {
  User,
  UserNamePassword,
  UserResponse,
} from "../../types/user.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import PasswordModal from "./PasswordModal";
import { getProfile, KeycloakProfile } from "../../services/keycloak";
import { useDebounce } from "../../utils/hooks/useDebounce";
import UserRow from "./UserRow";
import { Theme } from "@mui/material/styles";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import RoleModal from "./RoleModal";
import ConfirmModal from "../ConfirmModal";

interface UserTableProps {
  triggerFetch: number;
  onOpenEditDrawer: (userId: string) => void;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

const COLUMN_COUNT = 8;
const SKELETON_ROW_COUNT = 10;
const SEARCH_DEBOUNCE_MS = 500;
const LIST_LIMIT = [10, 20, 50, 100];

const renderSkeletonRows = () => {
  return Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

const headerCellSx = {
  borderRight: (theme: Theme) => `1px solid ${theme.palette.draft.main}`,
};

export default function UserTable({
  triggerFetch,
  onOpenEditDrawer,
  setTriggerFetch,
}: UserTableProps) {
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [users, setUsers] = useState<User[]>([]);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [openModalRole, setOpenModalRole] = useState<boolean>(false);
  // The account whose enable / disable is waiting for confirmation.
  const [pendingToggleUser, setPendingToggleUser] = useState<User | null>(null);
  const [toggling, setToggling] = useState<boolean>(false);
  const [pendingResetUser, setPendingResetUser] = useState<User | null>(null);
  const [resetting, setResetting] = useState<boolean>(false);
  const [newCredentials, setNewCredentials] =
    useState<UserNamePassword | null>(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<User | null>(
    null,
  );
  const [deleting, setDeleting] = useState<boolean>(false);
  const { isMaster } = useUserRole();
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [userProfile, setUserProfile] = useState<KeycloakProfile>();

  useEffect(() => {
    const onGetProfile = async () => {
      const profile = await getProfile();
      setUserProfile(profile);
    };

    onGetProfile();
  }, []);

  const getUsers = useCallback(async () => {
    if (userProfile) {
      setLoadingList(true);
      try {
        const {
          data: { data },
        } = await getListUser(
          page,
          limit,
          userProfile?.attributes.branchCode?.[0] ?? "",
          debouncedSearch,
        );

        const formatted: User[] = data.rows.map((user: UserResponse) => ({
          id: user.id,
          username: user.username,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          roles: user.roles,
          branch: user.branchCode + " " + user.branchName,
          routeCode: user.routeCode,
          enabled: user.enabled,
        }));

        setUsers(formatted);
        setTotalCount(data.count);
      } finally {
        setLoadingList(false);
      }
    }
  }, [userProfile, page, limit, debouncedSearch]);

  // Reset to the first page whenever the search term changes so results aren't
  // hidden on a now-out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    getUsers();
  }, [triggerFetch, getUsers]);

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSortByName = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleOpenRoleModal = () => setOpenModalRole(true);

  const handleCloseRoleModal = () => setOpenModalRole(false);

  const handleChangeLimit = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleChangePage = (_: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleRequestToggle = (user: User) => setPendingToggleUser(user);

  const handleCloseToggle = () => {
    if (toggling) return;
    setPendingToggleUser(null);
  };

  const handleConfirmToggle = async () => {
    if (!pendingToggleUser) return;
    setToggling(true);
    try {
      await setUserEnabled(pendingToggleUser.id, !pendingToggleUser.enabled);
      setPendingToggleUser(null);
      setTriggerFetch((prev) => prev + 1);
    } catch {
      // The axios interceptor reports the failure; the dialog stays open for a retry.
    } finally {
      setToggling(false);
    }
  };

  const handleRequestReset = (user: User) => setPendingResetUser(user);

  const handleCloseReset = () => {
    if (resetting) return;
    setPendingResetUser(null);
  };

  const handleConfirmReset = async () => {
    if (!pendingResetUser) return;
    setResetting(true);
    try {
      const {
        data: { data },
      } = await resetUsernamePasswordUser(pendingResetUser.id);
      setPendingResetUser(null);
      setNewCredentials({ username: data.username, password: data.password });
    } catch {
      // The axios interceptor reports the failure; the dialog stays open for a retry.
    } finally {
      setResetting(false);
    }
  };

  const handleCloseCredentials = () => setNewCredentials(null);

  const handleRequestDelete = (user: User) => setPendingDeleteUser(user);

  const handleCloseDelete = () => {
    if (deleting) return;
    setPendingDeleteUser(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteUser) return;
    setDeleting(true);
    try {
      await deleteUser(pendingDeleteUser.id);
      setPendingDeleteUser(null);
      setTriggerFetch((prev) => prev + 1);
    } catch {
      // The axios interceptor reports the failure; the dialog stays open for a retry.
    } finally {
      setDeleting(false);
    }
  };

  const sortedUsers = useMemo(() => {
    if (!sortOrder) return users;
    return [...users].sort((first, second) => {
      const byName = first.name
        .toLowerCase()
        .localeCompare(second.name.toLowerCase());
      return sortOrder === "asc" ? byName : -byName;
    });
  }, [users, sortOrder]);

  const isDisabling = pendingToggleUser?.enabled ?? false;
  const toggleTitle = isDisabling
    ? "ยืนยันการปิดใช้งาน"
    : "ยืนยันการเปิดใช้งาน";
  const toggleDescription = isDisabling
    ? `ผู้ใช้ ${pendingToggleUser?.username} จะเข้าสู่ระบบไม่ได้จนกว่าจะเปิดใช้งานอีกครั้ง`
    : `ผู้ใช้ ${pendingToggleUser?.username} จะกลับมาเข้าสู่ระบบได้`;
  const isEmpty = !loadingList && sortedUsers.length === 0;

  return (
    <>
      <Paper>
        <Box sx={{ p: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="ค้นหาชื่อผู้ใช้ ชื่อ นามสกุล หรืออีเมล์"
            value={search}
            onChange={handleChangeSearch}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ maxWidth: 420 }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                bgcolor: (theme) => theme.palette.background.default,
              }}
            >
              <TableRow>
                <TableCell sx={headerCellSx}>ชื่อผู้ใช้</TableCell>
                <TableCell
                  onClick={handleSortByName}
                  sx={{ ...headerCellSx, cursor: "pointer" }}
                >
                  ชื่อ ↑↓
                </TableCell>
                <TableCell sx={headerCellSx}>อีเมล์</TableCell>
                <TableCell sx={headerCellSx}>สาขา</TableCell>
                <TableCell sx={headerCellSx}>สาย</TableCell>
                <TableCell sx={headerCellSx}>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography>ตำแหน่ง</Typography>
                    <InfoIcon
                      sx={{
                        cursor: "pointer",
                        color: "#5c69e8",
                        fontSize: "20px",
                      }}
                      onClick={handleOpenRoleModal}
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={headerCellSx}>สถานะ</TableCell>
                <TableCell>การดำเนินการ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingList && renderSkeletonRows()}
              {!loadingList &&
                sortedUsers.map((user, index) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    striped={index % 2 === 1}
                    onEdit={onOpenEditDrawer}
                    onToggleEnabled={handleRequestToggle}
                    onResetPassword={handleRequestReset}
                    onDelete={isMaster ? handleRequestDelete : undefined}
                  />
                ))}
              {isEmpty && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center">
                    ไม่พบข้อมูลผู้ใช้งาน
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2">แสดง</Typography>
            <Select size="small" value={limit} onChange={handleChangeLimit}>
              {LIST_LIMIT.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="body2">รายการ</Typography>
          </Stack>
          <Pagination
            count={Math.ceil(totalCount / limit)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      </Paper>
      <ConfirmModal
        title={toggleTitle}
        description={toggleDescription}
        open={Boolean(pendingToggleUser)}
        loading={toggling}
        confirmColor={isDisabling ? "error" : "primary"}
        onClose={handleCloseToggle}
        onConfirm={handleConfirmToggle}
      />
      <ConfirmModal
        title="ยืนยันการรีเซ็ตรหัสผ่าน"
        description={`ระบบจะสร้างรหัสผ่านใหม่ให้ผู้ใช้ ${pendingResetUser?.username} และรหัสผ่านเดิมจะใช้ไม่ได้อีก`}
        open={Boolean(pendingResetUser)}
        loading={resetting}
        confirmColor="warning"
        onClose={handleCloseReset}
        onConfirm={handleConfirmReset}
      />
      <PasswordModal
        title="รีเซ็ตรหัสผ่านสำเร็จ"
        username={newCredentials?.username ?? ""}
        password={newCredentials?.password ?? ""}
        open={Boolean(newCredentials)}
        onClose={handleCloseCredentials}
      />
      <ConfirmModal
        title="ยืนยันการลบผู้ใช้"
        description={`ผู้ใช้ ${pendingDeleteUser?.username} จะถูกลบถาวรและกู้คืนไม่ได้ หากต้องการหยุดใช้ชั่วคราวให้ใช้ "ปิดใช้งาน" แทน`}
        open={Boolean(pendingDeleteUser)}
        loading={deleting}
        confirmColor="error"
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />
      <RoleModal open={openModalRole} onClose={handleCloseRoleModal} />
    </>
  );
}
